'use client'

import { useEffect, useState } from 'react'
import { Clock } from 'lucide-react'
import { MascotMedia } from '@/components/mascot-media'
import { UPGRADE_END, UPGRADE_START, UPGRADE_WINDOW_LABEL } from '@/lib/upgrade-window'

const DISSOLVE_MS = 5000
const INTERACTIVE_AFTER_MS = 2500

/**
 * Upgrade notice that takes over the screen: the mascot and message fade in while
 * the page behind them gradually dissolves into the background. It is purely a
 * visual layer — the app and its API calls keep running underneath. It lives in the
 * root layout, so it survives client-side navigation and only replays on a refresh.
 * There is deliberately no way to dismiss it.
 */
export function UpgradeTakeover() {
    const [phase, setPhase] = useState<'off' | 'idle' | 'covering'>('off')
    const [interactive, setInteractive] = useState(false)
    const [inProgress, setInProgress] = useState(false)

    useEffect(() => {
        if (Date.now() >= UPGRADE_END.getTime()) return
        setInProgress(Date.now() >= UPGRADE_START.getTime())
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        setPhase('idle')
        // Next frame, so the transition runs from the page's normal look.
        const start = window.setTimeout(() => setPhase('covering'), reduce ? 0 : 400)
        const unlock = window.setTimeout(() => setInteractive(true), reduce ? 0 : INTERACTIVE_AFTER_MS)
        return () => {
            window.clearTimeout(start)
            window.clearTimeout(unlock)
        }
    }, [])

    if (phase === 'off') return null

    const covered = phase === 'covering'

    return (
        <div
            role="alertdialog"
            aria-label="SATBuddy is being upgraded"
            className={`fixed inset-0 z-[100] flex flex-col items-center justify-center px-6 text-center ${
                interactive && covered ? 'pointer-events-auto' : 'pointer-events-none'
            }`}
        >
            {/* The dissolve: an opaque layer of the page background fading in over everything. */}
            <div
                className="absolute inset-0 bg-background"
                style={{
                    opacity: covered ? 1 : 0,
                    transition: `opacity ${DISSOLVE_MS}ms ease-in-out`,
                }}
            />
            <div
                className="relative flex flex-col items-center gap-5"
                style={{
                    opacity: covered ? 1 : 0,
                    transform: covered ? 'none' : 'translateY(12px) scale(0.97)',
                    transition: `opacity 1400ms ease-out 300ms, transform 1400ms ease-out 300ms`,
                }}
            >
                <MascotMedia className="block w-64 sm:w-80 md:w-96" />
                <div className="space-y-3">
              
                    <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-4xl">
                        We&apos;re upgrading SATBuddy
                    </h2>
                    <p className="mx-auto max-w-md text-sm text-muted-foreground sm:text-base">
                        {inProgress ? 'The platform is paused while we upgrade.' : 'The platform will be paused while we upgrade.'}{' '}
                        We&apos;ll be back better than before.
                    </p>
                    <div className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Clock className="h-4 w-4 text-primary" />
                        {UPGRADE_WINDOW_LABEL}
                    </div>
                </div>
            </div>
        </div>
    )
}
